<script setup lang="ts">
import { addHours, parse, format } from 'date-fns'

// Idioma
const { locale, t } = useI18n()
var browserLang = navigator?.language ?? locale.value

// Props
const props = defineProps({
  data: Object,
  arrayHoras: Object,
  urlParams: String,
  alternative: Boolean
})

const posthog = usePostHog()
const searchConversionVariant = ref<string>('control')
onMounted(() => {
  if (posthog) {
    posthog.onFeatureFlags(function () {
      if (posthog.getFeatureFlag('search_conversion_v1') == 'test') {
        searchConversionVariant.value = 'test'
      }
    })
  }
})

// Promo
const uniquePromos: ComputedRef<string[]> = computed(() => Array.from(new Set((props.data?.promo ?? []).map((p: Promo) => p.short_description))))

// Imagenees
const images = computed(() => props.data?.images)

// Hover event
const hoverCarousel = ref(false)
const hoverCard = ref(false)

const emit = defineEmits(['hoverHotel', 'toggle-favorite'])
watch(hoverCard, (newValue) => {
  if (newValue) {
    emit('hoverHotel', props.data?.id)
  } else {
    emit('hoverHotel', 0)
  }
})

const deviceStore = useDeviceStore();
const isMobile = deviceStore.isMobile;

// PostHog funnel
const { trackHotelCardClicked } = usePosthogFunnel()
const currencyStore = useCurrencyStore()
const { currency } = storeToRefs(currencyStore)

function trackHotelCardClick() {
  if (!props?.data?.id) return
  const minPrice = props.data?.pack_min_price?.[3]?.price ?? props.data?.pack_min_price?.[6]?.price ?? props.data?.pack_min_price?.[24]?.price
  trackHotelCardClicked({
    hotel_id: props.data.id,
    hotel_name: props.data?.name,
    date: undefined,
    currency: currency.value,
    min_price: minPrice ?? undefined,
    is_mobile: isMobile
  })
}

const openModalPromo = ref(false)
const openModalPiscina = ref(false)

const sumarHoras = (hora: string, cantidad: number): string => {
  // Detecta si es formato 12h con AM/PM
  const is12h = /AM|PM/i.test(hora)
  let date
  if (is12h) {
    date = parse(hora, 'hh:mm a', new Date())
  } else {
    date = parse(hora, 'HH:mm', new Date())
  }
  const nueva = addHours(date, cantidad)
  return is12h ? format(nueva, 'hh:mm a') : format(nueva, 'HH:mm')
}

const horasScroll = ref<HTMLElement | null>(null)
const promosScroll = ref<HTMLElement | null>(null)

const scrollPromos = (direction: 'left' | 'right') => {
  if (!promosScroll.value) return
  const scrollAmount = 200
  promosScroll.value.scrollBy({
    left: direction === 'left' ? -scrollAmount : scrollAmount,
    behavior: 'smooth'
  })
}

const open = ref(false)
const activeIndex = ref(0)
function onSelect(index: number) {
  activeIndex.value = index
}

// Computed property for available hours
const availableHours = computed(() => {
  return [3, 6, 24].filter(hour => props.arrayHoras?.[hour]?.length)
})

/** Mínimo de habitaciones disponibles en cualquier pack del hotel (el cuello de botella). */
const minRoomsAvailable = computed(() => {
  const rooms = props.data?.rooms ?? {}
  let total = 0
  let found = false
  for (const roomId of Object.keys(rooms)) {
    const packs = (rooms as Record<string, { packs?: Record<string, { num_rooms_available?: number }> }>)[roomId]?.packs ?? {}
    let roomMin = Infinity
    for (const packKey of ['3', '6', '24']) {
      const n = packs[packKey]?.num_rooms_available
      if (typeof n === 'number' && n < roomMin) roomMin = n
    }
    if (roomMin !== Infinity) {
      total += roomMin
      found = true
    }
  }
  return found ? total : 0
})

/** Umbral: mostrar badge de urgencia si quedan menos de esta cantidad. */
const URGENCY_THRESHOLD = 2
const showUrgencyBadge = computed(() => {
  const n = minRoomsAvailable.value
  return n > 0 && n < URGENCY_THRESHOLD
})

/** Parsea una hora "HH:mm" o "hh:mm AM/PM" a minutos desde medianoche (0-1439). */
function parseTimeToMinutes(timeStr: string): number {
  if (!timeStr || typeof timeStr !== 'string') return 0
  const hasAmPm = /AM|PM/i.test(timeStr)
  if (hasAmPm) {
    const [h, m] = timeStr.replace(/\s*(AM|PM)/i, '').trim().split(':').map(Number)
    const isPm = /PM/i.test(timeStr)
    const hours = (h ?? 0) % 12 + (isPm ? 12 : 0)
    return (hours * 60) + (m ?? 0)
  }
  const [h, m] = timeStr.split(':').map(Number)
  return ((h ?? 0) * 60) + (m ?? 0)
}

/** Formatea minutos al mismo estilo que los botones actuales: 24h → "10h", 12h → "10AM". */
function formatMinutesToButtonLabel(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24
  if (!changeHourFormat(browserLang)) {
    const pad = (n: number) => n.toString().padStart(2, '0')
    return `${pad(h)}h`
  }
  const hour12 = h % 12 || 12
  const suffix = h < 12 ? 'AM' : 'PM'
  return `${hour12}${suffix}` // sin cero a la izquierda, igual que los botones
}

/** Rango global min-max de horas disponibles (para variante test del A/B). Mismo formato que los botones: "10h - 20h" o "8AM - 8PM". */
const globalHoursRangeLabel = computed(() => {
  const arr = props.arrayHoras
  if (!arr) return ''
  let minM = Infinity
  let maxM = 0
  for (const key of [3, 6, 24]) {
    const times: string[] = arr[key] ?? []
    for (const t of times) {
      const m = parseTimeToMinutes(t)
      if (m < minM) minM = m
      if (m > maxM) maxM = m
    }
  }
  if (minM === Infinity || maxM === 0) return ''
  const minStr = formatMinutesToButtonLabel(minM)
  const maxStr = formatMinutesToButtonLabel(maxM)
  if (minStr === maxStr) return minStr
  return `${minStr} - ${maxStr}`
})

// Favorites Store
const favoritesStore = useFavoritesStore()
const { favoriteHotels } = storeToRefs(favoritesStore)

// Estado local del favorito
const favorite = computed(() => {
  return favoritesStore.isFavorite(props?.data?.id)
})

</script>

<template>
  <div class="relative h-full gap-4 sm:gap-3 flex flex-col sm:flex-row rounded-2xl" @mouseover="hoverCard = true"
    @mouseleave="hoverCard = false">

    <div :class="[
      'relative sm:w-5/12 lg:w-4/12 2xl:w-5/12 rounded-2xl bg-neutral-100 shrink-0',
      hoverCard
        ? 'shadow-[0px_0px_20px_0_rgba(0,0,0,0.1)]'
        : 'shadow-[0px_0px_10px_0_rgba(0,0,0,0.1)]'
    ]" :style="{
      transition: 'box-shadow 0.3s cubic-bezier(0.4,0,0.2,1)'
    }">
      <!-- Carousel -->
      <UCarousel :arrows="!isMobile && hoverCarousel ? true : false" @mouseover="hoverCarousel = true"
        @mouseleave="hoverCarousel = false" v-slot="{ item }" :items="images" :class="`h-full sm:min-h-44`"
        :next="{ size: 'xs', variant: 'soft' }" :prev="{ size: 'xs', variant: 'soft' }" :ui="{
          item: 'basis-full h-full',
          container: 'h-full',
          viewport: 'aspect-16/9 w-full h-full rounded-2xl',
          next: 'sm:end-2',
          prev: 'sm:start-2'
        }" @select="onSelect">
        <NuxtImg provider="byhours" format="webp" width="600" height="400" :src="generateImgUrl(String(item ?? ''))"
          @click="trackHotelCardClick(); navigateTo(`/${locale}/${props?.data?.slug || slugify(props?.data?.name || 'hotel')}-${props?.data?.id}.html?${urlParams ?? ''}`, { open: { target: alternative ? '_self' : '_blank' } })"
          :alt="`Imagen hotel`" class="w-full h-full object-cover cursor-pointer rounded-2xl" draggable="false"
          loading="lazy" />
      </UCarousel>

      <!-- Indicador de posición -->
      <div v-if="isMobile && images?.length > 1" class="absolute bottom-2 right-2 z-10">
        <div class="bg-neutral-900/70 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
          {{ activeIndex + 1 }}/{{ images?.length }}
        </div>
      </div>

      <!-- Horas disponibles -->
      <div class="absolute top-0 left-0 p-2 z-10 flex justify-between w-full">
        <UModal v-model:open="open" :ui="{ content: 'max-w-2xl max-h-[80vh] overflow-y-auto' }"
          :title="$t('labels.horasDisponibles')">
          <UButton :label="$t('buttons.dispo')" color="neutral" variant="soft" size="xs" icon="i-lucide-clock"
            @click.stop.prevent="open = true" class="shrink-0" :ui="{
              base: 'drop-shadow-md'
            }" />
          <template #body>
            <div class="text-xs">
              <div v-for="key in availableHours" :key="key">
                <div v-if="arrayHoras?.[key]?.length" class="pb-6">
                  <div class="font-bold mb-2">{{ key }}h</div>
                  <div class="flex flex-wrap gap-2 sm:gap-3">
                    <div v-for="item in arrayHoras?.[key]">
                      <UButton :label="arrayHoras?.[key]?.length === 1 ? item : `${item} - ${sumarHoras(item, key)}`"
                        color="neutral" variant="outline" size="xs" @click="() => {
                          trackHotelCardClick();
                          navigateTo(`/${locale}/${props?.data?.slug || slugify(props?.data?.name || 'hotel')}-${props?.data?.id}.html?${urlParams ?? ''}&pack=${key}&hora=${item}`, { open: { target: alternative ? '_self' : '_blank' } })
                        }" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </UModal>
      </div>

      <!-- Botón de favorito -->
      <div class="absolute top-0 right-0 p-2 z-10">
        <UButton :aria-label="$t('labels.aria.favorito')"
          :icon="favorite ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'" color="neutral" size="sm" variant="soft"
          class="hover:scale-105 transition-all duration-300" :ui="{
            base: 'drop-shadow-md',
            leadingIcon: favorite ? 'text-error' : 'text-neutral-900'
          }" @click.stop.prevent="emit('toggle-favorite', props?.data?.id)" />
      </div>

    </div>

    <transition name="fade-card" mode="out-in" :duration="300">
      <NuxtLink
        :to="`/${locale}/${props?.data?.slug || slugify(props?.data?.name || 'hotel')}-${props?.data?.id}.html?${urlParams ?? ''}`"
        :target="alternative ? '_self' : '_blank'" @click="trackHotelCardClick" :class="[
          'bg-transparent px-0 sm:bg-white sm:p-5',
          'transition-all duration-300 w-full sm:w-7/12 lg:w-8/12 flex flex-col sm:justify-between rounded-2xl min-w-0',
          'shadow-none',
          !isMobile ? 'hover:shadow-[0px_0px_20px_0_rgba(0,0,0,0.1)]' : 'hover:shadow-none',
          'sm:shadow-[0px_0px_10px_0_rgba(0,0,0,0.1)]'
        ]" :aria-label="props?.data?.name">
        <div class="flex flex-col gap-0.5 min-w-0 sm:flex-1">

          <!-- Info hotel -->
          <div class="flex justify-between items-center gap-2">

            <div class="flex gap-2 items-center -mt-1 min-w-0 flex-1">
              <div v-if="splitEstrellas(props?.data?.stars?.abbr).countEstrellas" class="shrink-0">
                <UIcon :key="index" v-for="index in splitEstrellas(props?.data?.stars?.abbr).countEstrellas"
                  name="i-heroicons-star-solid" class="text-xs" />
                <span class="text-sm">{{ splitEstrellas(props?.data?.stars?.abbr).rightString }}</span>
              </div>
              <p class="text-xs text-neutral-500 truncate">{{ data?.city }}</p>
            </div>

            <!-- Promos and Tags -->
            <div class="flex items-center gap-1 shrink-0 -mt-2 sm:-mt-4 sm:-mr-2.5">
              <div v-if="searchConversionVariant === 'control'">
                <div v-if="isMobile && uniquePromos?.length">
                  <UModal v-model:open="openModalPromo">
                    <UBadge @touchend.prevent.stop="openModalPromo = true"
                      :aria-label="$t('strapi.searchPage.filtroPromo')" color="secondary" variant="soft" size="sm"
                      icon="i-lucide-tag" :label="$t('strapi.searchPage.filtroPromo')" />
                    <template #header>
                      <p class="font-bold">{{ $t('strapi.searchPage.filtroPromo') }}</p>
                    </template>
                    <template #body>
                      <p v-for="item in uniquePromos" class="py-2">{{ item }}</p>
                    </template>
                  </UModal>
                </div>
                <div v-else v-if="uniquePromos?.length">
                  <UTooltip :delay-duration="100" :text="$t('strapi.searchPage.filtroPromo')" :content="{ side: 'top' }"
                    :ui="{ content: 'h-fit py-0' }">
                    <UBadge @touchend.prevent.stop="openModalPromo = true"
                      :aria-label="$t('strapi.searchPage.filtroPromo')" color="secondary" variant="soft" size="sm"
                      icon="i-lucide-tag" :label="$t('strapi.searchPage.filtroPromo')" />
                    <template #content>
                      <div class="p-1 text-xs">
                        <p v-for="item in uniquePromos" class="py-1">{{ item }}</p>
                      </div>
                    </template>
                  </UTooltip>
                </div>
              </div>
              <template v-for="item in data?.tag">

                <!-- Tag Top hotel -->
                <div v-if="item.id === 7" class="relative">
                  <UBadge :label="item.name" color="warning" variant="soft" size="sm" class="shine-effect" />
                  <div class="star">
                    <UIcon name="i-lucide-sparkle" class="text-2xs" />
                  </div>
                </div>

                <!-- Tag piscina -->
                <div v-else-if="item.id === 25">
                  <div v-if="isMobile">
                    <UModal v-model:open="openModalPiscina">
                      <UBadge @touchend.prevent.stop="openModalPiscina = true" :label="item.name" color="info"
                        variant="soft" size="sm" class="shine-effect hover:cursor-pointer" icon="ic:baseline-pool" />
                      <template #header>
                        <p class="font-bold">{{ item?.name }}</p>
                      </template>
                      <template #body>
                        <p class="py-2">
                          {{ $t('labels.tags.piscina') }}
                        </p>
                      </template>
                    </UModal>
                  </div>
                  <div v-else>
                    <UTooltip :delay-duration="100" :text="$t('labels.tags.piscina')" :content="{ side: 'top' }">
                      <UBadge :label="item.name" color="info" variant="soft" size="sm"
                        class="shine-effect hover:cursor-pointer" icon="ic:baseline-pool" />
                    </UTooltip>
                  </div>
                </div>

                <!-- Tag Spa  -->
                <div v-else-if="item.id === 27">
                  <UBadge :label="item.name" color="info" variant="soft" size="sm"
                    class="shine-effect hover:cursor-pointer" icon="ic:baseline-spa" />
                </div>

                <!-- Tag Amor amistad  -->
                <div v-else-if="item.id === 28">
                  <UBadge :label="item.name" variant="soft" size="sm" class="shine-effect hover:cursor-pointer"
                    icon="i-heroicons-heart-solid" :ui="{
                      leadingIcon: 'text-pink-500',
                      base: 'text-pink-500 bg-pink-500/10'
                    }" />
                </div>

                <!-- Tag normal -->
                <UBadge v-else :label="item.name" color="neutral" size="sm" variant="outline" />
              </template>
            </div>
          </div>

          <p class="text-base sm:text-lg font-bold mb-1.5 line-clamp-2">{{ data?.name }}</p>

          <!-- Urgencia: pocas habitaciones para esta fecha -->
          <UBadge v-if="showUrgencyBadge && !uniquePromos.length" color="primary" variant="soft" size="md"
            leading-icon="i-lucide-clock"
            :label="minRoomsAvailable === 1 ? t('searchCard.urgency.oneRoom') : t('searchCard.urgency.moreRooms', { count: minRoomsAvailable })"
            class="self-start" />

          <div v-if="searchConversionVariant === 'test'" class="relative w-full group min-w-0 flex items-center">
            <!-- Flecha Izquierda -->
            <div v-if="!isMobile && uniquePromos.length > 1"
              class="absolute left-0 -top-2 bottom-0 z-10 w-12 flex items-center justify-start bg-linear-to-r from-white to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
              <UButton icon="i-lucide-arrow-left" color="neutral" variant="solid" size="xs"
                class="pointer-events-auto ml-0.5 shadow-sm backdrop-blur-sm rounded-full"
                @click.prevent.stop="scrollPromos('left')" />
            </div>

            <!-- Contenedor Scrollable -->
            <div v-if="uniquePromos.length || showUrgencyBadge" ref="promosScroll"
              class="flex items-center w-full overflow-x-auto scrollbar-hide mb-2 min-w-0 gap-2 pr-8">
              <div v-if="uniquePromos.length" v-for="uniquePromo in uniquePromos" :key="uniquePromo"
                class="flex gap-1 shrink-0">
                <UBadge :aria-label="$t('strapi.searchPage.filtroPromo')" color="secondary" variant="soft" size="md"
                  :label="uniquePromo" class="shrink-0" />
              </div>
            </div>

            <!-- Flecha Derecha -->
            <div v-if="!isMobile && uniquePromos.length > 1"
              class="absolute right-0 -top-2 bottom-0 z-10 w-12 flex items-center justify-end bg-linear-to-l from-white to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
              <UButton icon="i-lucide-arrow-right" color="neutral" variant="solid" size="xs"
                class="pointer-events-auto mr-0.5 shadow-sm backdrop-blur-sm rounded-full"
                @click.prevent.stop="scrollPromos('right')" />
            </div>
          </div>

          <div class="flex w-full gap-4 items-center mb-1">
            <div v-if="globalHoursRangeLabel">
              <UButton v-if="globalHoursRangeLabel" color="neutral" size="sm" :ui="{ base: 'px-5 py-2.5 shrink-0' }"
                variant="outline" leading-icon="i-lucide-door-open" :label="globalHoursRangeLabel" @click.prevent.stop="() => {
                  trackHotelCardClick();
                  navigateTo(`/${locale}/${props?.data?.slug || slugify(props?.data?.name || 'hotel')}-${props?.data?.id}.html?${urlParams ?? ''}&scrollTo=seccion_habitaciones`, { open: { target: alternative ? '_self' : '_blank' } })
                }" />
            </div>

            <!-- Propuesta valor -->
            <div v-if="searchConversionVariant === 'test'" class="flex flex-wrap gap-x-4 pt-0.5 flex-col">
              <div class="flex items-center gap-1">
                <UIcon name="i-lucide-badge-check" class="w-3.5 h-3.5 text-success-500" />
                <span class="text-[10px] text-success-500">{{ $t('roomCard.badges.freeCancellation') }}</span>
              </div>
              <div class="flex items-center gap-1">
                <UIcon :name="data?.tpv ? 'i-lucide-credit-card' : 'i-lucide-building-2'"
                  class="w-3.5 h-3.5 text-success-500" />
                <span class="text-[10px] text-success-500">{{ data?.tpv ? $t('roomCard.badges.payNow') :
                  $t('roomCard.badges.payAtDestination') }}</span>
              </div>
            </div>
          </div>

        </div>

        <!-- Tabla de precios -->
        <div class="mt-2 sm:mt-3 shrink-0">
          <div class="grid grid-rows-1 grid-flow-col grid-cols-3 gap-3">
            <div v-if="data?.pack_min_price?.[3]?.price_label">
              <div class="text-sm sm:text-sm text-neutral-500">3 {{ $t('labels.horas') }}</div>
              <div class="text-lg/6 sm:text-lg font-semibold">{{ data?.pack_min_price?.[3]?.price_label }}
              </div>
            </div>
            <div v-if="data?.pack_min_price?.[6]?.price_label">
              <div class="text-sm sm:text-sm text-neutral-500">6 {{ $t('labels.horas') }}</div>
              <div class="text-lg/6 sm:text-lg font-semibold">{{ data?.pack_min_price?.[6]?.price_label }}
              </div>
            </div>
            <div v-if="data?.pack_min_price?.[24]?.price_label">
              <div class="text-sm sm:text-sm text-neutral-500">24 {{ $t('labels.horas') }}</div>
              <div class="text-lg/6 sm:text-lg font-semibold">{{ data?.pack_min_price?.[24]?.price_label }}
              </div>
            </div>
          </div>
        </div>
      </NuxtLink>
    </transition>

  </div>
</template>

<style scoped>
.shine-effect {
  position: relative;
  overflow: hidden;
}

.shine-effect::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(to right,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent);
  transform: skewX(-25deg);
  animation: shine 15s infinite;
  z-index: 2;
}

.star {
  position: absolute;
  top: -4px;
  right: -2px;
  font-size: 11px;
  color: #fbbf24;
  opacity: 1;
  z-index: 1;
}

@keyframes shine {
  0% {
    left: -100%;
  }

  8% {
    left: 100%;
  }

  18% {
    left: -100%;
  }

  26% {
    left: 100%;
  }

  36% {
    left: -100%;
  }

  44% {
    left: 100%;
  }

  54% {
    left: -100%;
  }

  62% {
    left: 100%;
  }

  100% {
    left: 100%;
  }
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@keyframes heartbeat {

  0%,
  100% {
    transform: scale(1);
  }

  25% {
    transform: scale(1.2);
  }

  50% {
    transform: scale(0.95);
  }

  75% {
    transform: scale(1.1);
  }
}

.heartbeat-animation {
  animation: heartbeat 2s ease-in-out;
}
</style>